/**
 * 图片高度限制工具 - 基于容器宽度动态计算图片最大高度
 *
 * 核心原则:
 * 1. 图片高度 ≤ 富文本编辑器容器宽度 × 配置百分比（默认 70%，后台「图片尺寸设置」可调）
 * 2. 图片默认使用 display: inline-block 在同一行显示(由CSS控制)
 * 3. 图片相邻处不留缝隙(由CSS margin:0 控制)
 */
import { supabase } from '../supabase/client';

/** 图片尺寸配置契约（exam_settings.image_size_settings JSONB） */
export interface ImageSizeSettings {
  /** 单图高度上限（容器宽度百分比，10~100，默认 70） */
  maxHeightPercent: number;
  /** 多图组单图宽度上限（容器宽度百分比，10~100，默认 45） */
  multiMaxWidthPercent: number;
  /** 单图组图片宽度上限（容器宽度百分比，10~100，默认 100） */
  singleMaxWidthPercent: number;
  /** 多图组排列方式：twoPerRow 一行两图（默认）/ onePerRow 一行一图 */
  multiImageLayout: 'twoPerRow' | 'onePerRow';
}

export const IMAGE_SIZE_DEFAULTS: ImageSizeSettings = { maxHeightPercent: 70, multiMaxWidthPercent: 45, singleMaxWidthPercent: 100, multiImageLayout: 'twoPerRow' };
const IMAGE_SIZE_CACHE_KEY = 'image_size_settings_cache';

/** 校验单个百分比：10~100 整数 */
function isValidPercent(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 10 && v <= 100;
}

/** 校验多图组排列方式枚举值 */
function isValidLayout(v: unknown): v is 'twoPerRow' | 'onePerRow' {
  return v === 'twoPerRow' || v === 'onePerRow';
}

/** 归一化任意来源的配置（含 localStorage 脏值兜底） */
function normalizeConfig(raw: any): ImageSizeSettings {
  return {
    maxHeightPercent: isValidPercent(raw?.maxHeightPercent) ? raw.maxHeightPercent : IMAGE_SIZE_DEFAULTS.maxHeightPercent,
    multiMaxWidthPercent: isValidPercent(raw?.multiMaxWidthPercent) ? raw.multiMaxWidthPercent : IMAGE_SIZE_DEFAULTS.multiMaxWidthPercent,
    singleMaxWidthPercent: isValidPercent(raw?.singleMaxWidthPercent) ? raw.singleMaxWidthPercent : IMAGE_SIZE_DEFAULTS.singleMaxWidthPercent,
    multiImageLayout: isValidLayout(raw?.multiImageLayout) ? raw.multiImageLayout : IMAGE_SIZE_DEFAULTS.multiImageLayout,
  };
}

/** 模块级内存缓存：启动先用 localStorage（或默认值），init 拉库后覆盖 */
let imageSizeCache: ImageSizeSettings = (() => {
  try {
    const cached = localStorage.getItem(IMAGE_SIZE_CACHE_KEY);
    return cached ? normalizeConfig(JSON.parse(cached)) : { ...IMAGE_SIZE_DEFAULTS };
  } catch {
    return { ...IMAGE_SIZE_DEFAULTS };
  }
})();

/** 同步获取当前图片尺寸配置（内存缓存，失败回退默认值） */
export function getImageSizeSettings(): ImageSizeSettings {
  return imageSizeCache;
}

/** 本地更新缓存（设置页保存成功后调用，无需重新拉库） */
export function applyImageSizeSettings(settings: ImageSizeSettings): void {
  imageSizeCache = normalizeConfig(settings);
  try {
    localStorage.setItem(IMAGE_SIZE_CACHE_KEY, JSON.stringify(imageSizeCache));
  } catch { /* localStorage 不可用时忽略 */ }
}

/** 从 exam_settings.image_size_settings 异步加载配置并更新缓存（App 启动时调用一次；失败静默回退） */
export async function initImageSizeSettings(): Promise<void> {
  try {
    const { data, error } = await supabase
      .from('exam_settings')
      .select('image_size_settings')
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    const raw = data?.image_size_settings;
    if (raw && (isValidPercent(raw.maxHeightPercent) || isValidPercent(raw.multiMaxWidthPercent) || isValidPercent(raw.singleMaxWidthPercent) || isValidLayout(raw.multiImageLayout))) {
      applyImageSizeSettings(normalizeConfig(raw));
    }
  } catch (err) {
    console.warn('[imageHeightLimit] 加载图片尺寸设置失败（回退缓存/默认值）:', err);
  }
}

/**
 * 根据容器宽度,计算每张图片的最大高度
 * @param containerWidth - 富文本编辑器容器宽度(px)
 * @returns 每张图片的最大高度(px)
 */
export function calculateMaxImageHeight(containerWidth: number): number {
  // 第一原则: 高度 ≤ 容器宽度 × 配置百分比（默认 70%）
  const maxHeight = containerWidth * (getImageSizeSettings().maxHeightPercent / 100);
  return maxHeight;
}

/**
 * 跨层级判断两张图片之间是否有"有意义的文字"
 *
 * 有意义的文字 = trim 后非空的文字内容（文字、字母、数字、标点等）
 * 无意义内容 = 纯空白、<br>、空 <span>、空 <p>、空 <div>
 *
 * 实现思路：用 TreeWalker 遍历 container 内所有 TEXT_NODE，
 * 只收集 prevImg 之后、currImg 之前的文本节点，判断是否有非空内容。
 * 解决图片被 <p> 等容器包裹时 nextSibling = null 导致的误判。
 */
function hasMeaningfulTextBetween(
  prevImg: HTMLImageElement,
  currImg: HTMLImageElement,
  container: HTMLElement
): boolean {
  // 用 compareDocumentPosition 判断节点 A 是否在节点 B 之后
  // Node.DOCUMENT_POSITION_FOLLOWING = 4（A 在 B 之后）
  const isAfterPrev = (node: Node) =>
    (prevImg.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
  // Node.DOCUMENT_POSITION_PRECEDING = 2（A 在 B 之前）
  const isBeforeCurr = (node: Node) =>
    (currImg.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_PRECEDING) !== 0;

  // 用 TreeWalker 遍历 container 内所有 TEXT_NODE
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null
  );

  let node: Node | null;
  while ((node = walker.nextNode())) {
    // 只检查 prevImg 之后、currImg 之前的文本节点
    if (isAfterPrev(node) && isBeforeCurr(node)) {
      const text = node.textContent?.trim();
      if (text) {
        // 排除纯空白、纯 &nbsp; 等
        return true;
      }
    }
  }
  return false;
}

/**
 * 检测相邻图片是否属于同一组（连续排列且无文字间隔）
 *
 * 跨层级判断：用 compareDocumentPosition + TreeWalker 遍历
 * 解决图片被 <p>/<div> 等容器包裹时 nextSibling = null 导致的误判。
 */
function detectImageGroups(container: HTMLElement): HTMLImageElement[][] {
  const allImages = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
  if (allImages.length === 0) return [];

  const groups: HTMLImageElement[][] = [[allImages[0]]];

  for (let i = 1; i < allImages.length; i++) {
    const prevImg = allImages[i - 1];
    const currImg = allImages[i];

    // 跨层级判断两张图片之间是否有"有意义的文字"
    const hasTextBetween = hasMeaningfulTextBetween(prevImg, currImg, container);

    if (hasTextBetween) {
      groups.push([currImg]);
    } else {
      groups[groups.length - 1].push(currImg);
    }
  }

  return groups;
}

/**
 * 读取图片人为设置的宽度百分比
 * 判定依据：data-edit-w 是滑杆/拖拽缩放保存时写入的存储源，只有它（或编辑器实时预览中的
 * 非默认 style width）才代表用户主动调整过；解析器自动写入的 style width:50%（多图）/
 * 100%（默认）以及本模块自动写入的 data-auto-w 宽度不算人为调整。
 * @returns 未调整时返回 null
 */
function getUserWidthPercent(imgElement: HTMLImageElement): number | null {
  // 首选 data-edit-w（人为调整的存储源）
  const dataW = imgElement.getAttribute('data-edit-w');
  if (dataW) {
    const v = parseFloat(dataW);
    if (!Number.isNaN(v) && v > 0) return v;
  }
  // 解析当前 style width
  let styleW: number | null = null;
  const styleWidth = imgElement.style.width;
  if (styleWidth) {
    const m = styleWidth.match(/^(\d+(?:\.\d+)?)%$/);
    if (m) {
      const v = parseFloat(m[1]);
      if (v > 0) styleW = v;
    }
  }
  if (styleW === null) return null;
  // 本模块自动写入的宽度（data-auto-w 与当前值一致）→ 非人为调整
  const autoW = imgElement.getAttribute('data-auto-w');
  if (autoW && parseFloat(autoW) === styleW) return null;
  // 解析器默认值（50=多图 / 100=单图）→ 非人为调整
  if (styleW === 50 || styleW === 100) return null;
  // 其余值视为编辑器滑杆实时预览中的人为调整
  return styleW;
}

/**
 * 多图组块级段落 inline 化 / 恢复
 * 根因：Quill 常把相邻图片拆进各自独立的 <p>（块级），段落本身强制一行一图，宽度再小也没用。
 * 自动并排模式下把"仅含图片（无有意义文字）"的父段落 display:inline，使组内图片进入同一行内流；
 * 一行一图 / 单图 / 非并排配置时恢复原始 display（备份在 data-orig-display）。
 */
function setMultiGroupInline(img: HTMLImageElement, inline: boolean): void {
  const parent = img.parentElement;
  if (!parent || (parent.tagName !== 'P' && parent.tagName !== 'DIV')) return;
  // 段落内含有意义文字时不允许 inline 化（避免文字与前后内容混排）
  const hasText = Array.from(parent.childNodes).some(
    (n) => n.nodeType === Node.TEXT_NODE && n.textContent?.trim()
  );
  if (hasText) return;
  if (inline) {
    if (!parent.hasAttribute('data-orig-display')) {
      parent.setAttribute('data-orig-display', getComputedStyle(parent).display);
    }
    parent.style.display = 'inline';
  } else if (parent.style.display === 'inline' && parent.hasAttribute('data-orig-display')) {
    parent.style.display = parent.getAttribute('data-orig-display') || '';
    parent.removeAttribute('data-orig-display');
  }
}

/**
 * 对单张图片应用高度和宽度约束
 * 规则1（始终生效）：maxHeight = 容器宽度 × maxHeightPercent（默认 70%），高度受限时宽度按原始宽高比等比缩放
 * 规则2（仅多图组生效，multiImageLayout=twoPerRow 时）：未人为调整的图宽度写为 multiMaxWidthPercent（默认 45%），
 * 人为调整过的图尊重其宽度、同行放不下时由浏览器自然换行；onePerRow 时组内每张图按单图规则显示
 * 单图组：宽度受 singleMaxWidthPercent（默认 100%）与高度约束取更严者
 * @param isMultiImageGroup - 该图片是否属于多图组（组内有多张图片）
 * @param signal - AbortSignal，用于在组件卸载时取消待触发的 load 回调
 */
function applyConstraintsToImage(imgElement: HTMLImageElement, containerWidth: number, maxHeight: number, isMultiImageGroup: boolean, signal?: AbortSignal): void {
  // 如果已被中止（组件卸载），跳过
  if (signal?.aborted) return;

  const settings = getImageSizeSettings();

  // 规则1（始终生效）：设置最大高度
  imgElement.style.maxHeight = `${maxHeight}px`;

  // 如果图片已加载完成，根据宽高比计算宽度约束
  if (imgElement.complete && imgElement.naturalWidth > 0 && imgElement.naturalHeight > 0) {
    const aspectRatio = imgElement.naturalWidth / imgElement.naturalHeight;

    // 规则1：高度受限时，宽度按原始宽高比等比缩放
    // constrainedWidth = maxHeight × aspectRatio
    const constrainedWidth = maxHeight * aspectRatio;

    if (isMultiImageGroup) {
      // 单图宽度约束（一行一图模式下多图组每张图也按单图规则显示；也是人为调大宽度后的兜底容器上限）
      const maxWidthBySingle = containerWidth * (settings.singleMaxWidthPercent / 100);
      // 规则2（仅多图组生效，百分比后台可配，默认 45%）：
      // 多图组中图片 maxWidth 受限，同时满足高度约束
      const maxWidthByFlex = containerWidth * (settings.multiMaxWidthPercent / 100);
      // 读取人为设置的宽度百分比（编辑器滑杆 / data-edit-w），null 视为未调整
      const userWidthPercent = getUserWidthPercent(imgElement);
      if (settings.multiImageLayout === 'onePerRow') {
        // 一行一图：组内每张图按单图规则显示，父段落恢复块级
        setMultiGroupInline(imgElement, false);
        imgElement.style.maxWidth = `${Math.min(constrainedWidth, maxWidthBySingle)}px`;
      } else if (userWidthPercent !== null) {
        // 自动并排（可换行）+ 人为调过宽度：尊重用户设置的宽度，不再钳制到 multiMaxWidthPercent，
        // 仅受单图宽度上限兜底；同一行两张图总宽超过容器时由浏览器自然换行（右图换行显示，不压缩变形）
        setMultiGroupInline(imgElement, true);
        imgElement.style.maxWidth = `${maxWidthBySingle}px`;
      } else {
        // 未人为调整的图（含解析器默认 50%/100%）：直接把宽度写为 multiMaxWidthPercent（默认 45%），
        // 45%+45% 并排稳定成立（不受行内空白影响）；写 data-auto-w 标记保证幂等且不被误判为人为调整。
        // 同时把纯图片父段落 inline 化，避免 Quill 把两图拆进独立 <p> 导致块级段落强制一行一图。
        // 后台改配置后 ResizeObserver 重跑时会以新值覆盖（data-auto-w 旧值与 style 不匹配 → 仍走本分支）。
        setMultiGroupInline(imgElement, true);
        imgElement.style.width = `${settings.multiMaxWidthPercent}%`;
        imgElement.style.height = 'auto';
        imgElement.style.objectFit = 'contain';
        imgElement.setAttribute('data-auto-w', String(settings.multiMaxWidthPercent));
        imgElement.style.maxWidth = `${Math.min(constrainedWidth, maxWidthByFlex)}px`;
      }
    } else {
      // 单图组：宽度同时受高度约束（宽高比等比缩放）与独立宽度上限（后台可配，默认 100%）约束，取更严者
      const maxWidthBySingle = containerWidth * (settings.singleMaxWidthPercent / 100);
      imgElement.style.maxWidth = `${Math.min(constrainedWidth, maxWidthBySingle)}px`;
    }
  } else {
    // 图片未加载完成，监听 load 事件后重新应用约束
    // 使用 AbortSignal 确保组件卸载时自动移除此监听器
    const onLoad = () => {
      // 安全检查：图片必须仍在 DOM 中才应用约束
      if (!imgElement.isConnected) return;
      applyConstraintsToImage(imgElement, containerWidth, maxHeight, isMultiImageGroup, signal);
    };
    imgElement.addEventListener('load', onLoad, { once: true, signal });
  }
}

/**
 * 为指定容器内的图片应用高度限制
 * @param container - 目标容器
 * @param signal - 可选的 AbortSignal，用于在组件卸载时取消待触发的 load 回调
 */
export function applyImageHeightLimit(container: HTMLElement, signal?: AbortSignal): void {
  // 如果已被中止或容器已不在文档树中，跳过
  if (signal?.aborted) return;
  if (!container.isConnected) return;

  const containerWidth = container.offsetWidth;

  // 容器宽度为0时跳过计算,避免除零错误
  if (containerWidth === 0) return;

  const maxHeight = calculateMaxImageHeight(containerWidth);

  // 先处理带 data-img-count 属性的图片（来自 Word 解析）
  // data-img-count 表示该题干共有多少张图片，count > 1 时为多图组
  const markedImages = container.querySelectorAll('img[data-img-count]');
  markedImages.forEach(img => {
    const imgCount = parseInt((img as HTMLImageElement).getAttribute('data-img-count') || '1');
    const isMultiImageGroup = imgCount > 1;
    applyConstraintsToImage(img as HTMLImageElement, containerWidth, maxHeight, isMultiImageGroup, signal);
  });

  // 再处理没有 data-img-count 属性的图片（手动上传）
  // 注意：detectImageGroups 遍历全部 img（含 marked，用于正确判定分组边界），
  // 但组内循环跳过已处理的 marked 图片，避免重复应用约束
  const unmarkedImages = container.querySelectorAll('img:not([data-img-count])');
  if (unmarkedImages.length === 0) return;

  // 根据图片组大小判断是否为多图组：组内有多张图片时 isMultiImageGroup = true
  const groups = detectImageGroups(container);
  groups.forEach(group => {
    const isMultiImageGroup = group.length > 1;
    group.forEach(img => {
      if (img.hasAttribute('data-img-count')) return; // 已在上方 markedImages 分支处理
      applyConstraintsToImage(img, containerWidth, maxHeight, isMultiImageGroup, signal);
    });
  });
}

/**
 * 监听容器大小变化,动态更新图片高度限制
 * @param container - 需要监听的容器元素
 * @returns 清理函数,用于组件卸载时断开观察并取消所有待触发的 load 回调
 */
export function watchImageHeight(container: HTMLElement): () => void {
  // AbortController 用于在清理时取消所有待触发的图片 load 回调
  const abortController = new AbortController();

  // 初始应用一次
  applyImageHeightLimit(container, abortController.signal);

  let rafId: number | null = null;

  const resizeObserver = new ResizeObserver(() => {
    // 如果容器已不在文档树中，立即断开观察器并返回
    if (!container.isConnected) {
      resizeObserver.disconnect();
      return;
    }
    // 使用 requestAnimationFrame 防抖，避免在同一帧内多次触发导致 loop 错误
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      try {
        applyImageHeightLimit(container, abortController.signal);
      } catch (error) {
        // 静默忽略 ResizeObserver 回调中的异常，避免影响页面稳定性
        console.warn('ResizeObserver callback error:', error);
      }
    });
  });

  resizeObserver.observe(container);

  // 返回清理函数：同时断开 ResizeObserver、取消 rAF 和取消所有 load 监听器
  return () => {
    abortController.abort();
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    resizeObserver.disconnect();
  };
}
